import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface TenantInitRequest {
  tenantCode: string;
  tenantName: string;
  adminUser: {
    username: string;
    password: string;
    email?: string;
  };
  services: {
    sapB1?: {
      serverUrl: string;
      username: string;
      password: string;
      company: string;
    };
    documentParsing?: {
      serverUrl: string;
      username: string;
      password: string;
    };
    chatbi?: {
      serverUrl: string;
      username: string;
      password: string;
    };
  };
  dataInit: {
    includeModeling: boolean;
    includeInstructions: boolean;
    includeTemplates: boolean;
    includePermissions: boolean;
    syncToRemote: boolean;
    syncModeling: boolean;
    syncInstructions: boolean;
    syncTemplates: boolean;
    syncPermissions: boolean;
  };
}

export interface InitStep {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  message?: string;
  error?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
}

export interface InitProgress {
  tenantCode: string;
  totalSteps: number;
  completedSteps: number;
  currentStep: string;
  status: 'initializing' | 'completed' | 'failed';
  steps: InitStep[];
  startTime: string;
  endTime?: string;
  totalDuration?: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

class TenantInitService {
  /**
   * 开始多租户初始化
   */
  async initializeTenant(request: TenantInitRequest): Promise<ApiResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/tenants/init`, request);
      return response.data;
    } catch (error: any) {
      console.error('初始化租户失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '初始化失败',
        error: error.message
      };
    }
  }

  /**
   * 获取初始化进度
   */
  async getInitProgress(tenantCode: string): Promise<InitProgress> {
    try {
      const response = await axios.get(`${API_BASE_URL}/tenants/${tenantCode}/init/progress`);
      return response.data.data;
    } catch (error: any) {
      console.error('获取初始化进度失败:', error);
      throw new Error(error.response?.data?.message || '获取进度失败');
    }
  }

  /**
   * 获取所有租户列表
   */
  async getAllTenants(): Promise<ApiResponse<any[]>> {
    try {
      const response = await axios.get(`${API_BASE_URL}/tenants`);
      return response.data;
    } catch (error: any) {
      console.error('获取租户列表失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '获取租户列表失败',
        error: error.message
      };
    }
  }

  /**
   * 检查租户代码是否可用
   */
  async checkTenantCodeAvailable(tenantCode: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await axios.get(`${API_BASE_URL}/tenants/check/${tenantCode}`);
      return response.data;
    } catch (error: any) {
      console.error('检查租户代码失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '检查租户代码失败',
        error: error.message
      };
    }
  }

  /**
   * 获取初始化模板配置
   */
  async getInitTemplates(): Promise<ApiResponse<any[]>> {
    try {
      const response = await axios.get(`${API_BASE_URL}/tenants/init/templates`);
      return response.data;
    } catch (error: any) {
      console.error('获取初始化模板失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '获取初始化模板失败',
        error: error.message
      };
    }
  }

  /**
   * 获取租户状态
   */
  async getTenantState(tenantCode: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.get(`${API_BASE_URL}/tenants/${tenantCode}/state`);
      return response.data;
    } catch (error: any) {
      console.error('获取租户状态失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '获取租户状态失败',
        error: error.message
      };
    }
  }

  /**
   * 验证服务连接
   */
  async validateServiceConnection(serviceType: 'sapB1' | 'documentParsing', config: any): Promise<ApiResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/tenants/validate-service`, {
        serviceType,
        config
      });
      return response.data;
    } catch (error: any) {
      console.error('验证服务连接失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '验证服务连接失败',
        error: error.message
      };
    }
  }
}

export const tenantInitService = new TenantInitService();
